import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import MoonIcon from '../../components/MoonIcon/MoonIcon';
import ShareRow from '../../components/ShareRow/ShareRow';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import { getPhaseOn, getSkyToday } from '../../services/skyService';
import { chartDateHref } from '../../components/DateFields/DateFields';
import { MOON_SIGNS, PHASES, PHASE_ORDER } from '../../data/astro/moonPhases';
import { SIGN_BY_NAME } from '../../data/astro/signs';
import '../Astro/Astro.css';
import './MoonPhase.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const FAQ = [
  {
    question: 'What phase is the moon today?',
    answer: 'The phase at the top of this page is calculated from the exact positions of the Sun and Moon right now, for your own timezone. New, quarter, and full moon names are used only on the calendar day the exact moment happens where you are, the way almanacs label them.',
  },
  {
    question: 'What does a full moon mean spiritually?',
    answer: 'In astrology the Full Moon is the peak of the lunar cycle, when the Sun and Moon sit opposite each other. It is read as a time of culmination, clarity, and strong feelings: what you started at the New Moon shows its results, and it is a natural moment to celebrate and to release what you have outgrown.',
  },
  {
    question: 'How long does each moon phase last?',
    answer: 'A full lunar cycle takes about 29.5 days. The New, First Quarter, Full, and Last Quarter moons are exact moments. The phases between them, the crescents and gibbous moons, each last about a week.',
  },
  {
    question: 'What does the moon sign mean?',
    answer: 'The Moon moves through all twelve zodiac signs in about 27 days, spending two to three days in each. Its sign colors the emotional tone of those days: Taurus slows things down and seeks comfort, Gemini makes the mind busy, Cancer turns attention to home, and so on.',
  },
  {
    question: 'Can I use the moon phases with tarot?',
    answer: 'Yes. Many readers pull cards at the New Moon to set intentions and again at the Full Moon to see what has come into the light. Each phase on this page has a card that fits its energy, and the daily tarot reading is a good companion for any phase.',
  },
];

function formatMoment(iso) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatTime(iso) {
  try {
    return new Intl.DateTimeFormat(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function nextLabel(sky) {
  const next = sky.upcoming?.find((item) => item.key !== sky.phase_key);
  if (!next) return '';
  const days = Math.round((new Date(next.at) - Date.now()) / 86400000);
  if (days <= 0) return `${next.phase} later today`;
  if (days === 1) return `${next.phase} tomorrow`;
  return `${next.phase} in ${days} days`;
}

const MoonPhase = () => {
  const [sky, setSky] = useState(null);
  const [skyError, setSkyError] = useState('');
  const [birth, setBirth] = useState({ month: '', day: '', year: '' });
  const [birthResult, setBirthResult] = useState(null);
  const [birthError, setBirthError] = useState('');
  const [birthBusy, setBirthBusy] = useState(false);

  useEffect(() => {
    getSkyToday().then(setSky).catch((err) => setSkyError(err.message || 'The sky is unavailable right now.'));
  }, []);

  const phase = sky ? PHASES[sky.phase_key] : null;
  const sign = sky ? SIGN_BY_NAME[sky.moon_sign] : null;

  const lookupBirthday = async (event) => {
    event.preventDefault();
    const month = Number(birth.month);
    const day = Number(birth.day);
    const year = Number(birth.year);
    const real = new Date(year, month - 1, day);
    if (!month || !day || !year || real.getMonth() !== month - 1 || real.getDate() !== day || year < 1900 || year > 2100) {
      setBirthError('Enter a real date from 1900 through 2100.');
      return;
    }
    setBirthError('');
    setBirthBusy(true);
    try {
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setBirthResult(await getPhaseOn(iso));
    } catch (err) {
      setBirthError(err.message || 'That date could not be looked up.');
    } finally {
      setBirthBusy(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Moon Phase Today',
        url: 'https://nowtarot.com/moon-phase-today/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: "Tonight's moon phase, the Moon's zodiac sign, upcoming new and full moons, and what each phase means spiritually.",
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  const birthPhase = birthResult ? PHASES[birthResult.phase_key] : null;

  return (
    <div className="astro-page moon-page">
      <Helmet>
        <title>Moon Phase Today: Tonight's Moon, Sign &amp; Meaning | NowTarot</title>
        <meta name="description" content="What phase is the moon today? See tonight's moon phase, its zodiac sign, the next new and full moon, and the spiritual meaning of each lunar phase. Free." />
        <link rel="canonical" href="https://nowtarot.com/moon-phase-today/" />
        <meta property="og:title" content="Moon Phase Today: Tonight's Moon, Sign & Meaning" />
        <meta property="og:description" content="Tonight's moon phase, the Moon's sign, upcoming lunations, and what each phase means." />
        <meta property="og:url" content="https://nowtarot.com/moon-phase-today/" />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <p className="astro-eyebrow">Tool · Free · Updated live</p>
        <h1>Moon phase <em>today</em></h1>

        <section className="moon-hero astro-card" aria-live="polite">
          <div className="moon-hero-icon">
            <MoonIcon angle={sky ? sky.angle : 180} size={190} title={sky ? `${sky.phase}, ${Math.round(sky.illumination * 100)} percent lit` : 'The moon'} />
          </div>
          <div className="moon-hero-text">
            {sky ? (
              <>
                <h2>{sky.phase}</h2>
                <p className="moon-stats">
                  <span><strong>{Math.round(sky.illumination * 100)}%</strong> lit</span>
                  <span><strong>{sky.age_days}</strong> days old</span>
                  <span>{sky.waxing ? 'Waxing ↑' : 'Waning ↓'}</span>
                </p>
                <p className="moon-sign-line">
                  {sign?.glyph} Moon in <strong>{sky.moon_sign}</strong>
                  {sky.next_sign && <> · moves into {sky.next_sign.sign} {formatTime(sky.next_sign.at)}</>}
                </p>
                <p className="astro-muted">{nextLabel(sky)}</p>
              </>
            ) : (
              <>
                <h2>{skyError ? 'The moon is hiding' : 'Reading the sky…'}</h2>
                <p className="astro-muted">{skyError || 'Calculating the exact phase for your timezone.'}</p>
              </>
            )}
          </div>
        </section>

        {phase && (
          <section className="astro-card" id="meaning">
            <p className="astro-eyebrow">What this phase means</p>
            <h2>The energy of the {phase.name}</h2>
            <ul className="astro-tags">
              {phase.keywords.map((word) => <li key={word}>{word}</li>)}
            </ul>
            <p><strong>{phase.energy}</strong></p>
            {phase.meaning.map((paragraph) => <p key={paragraph.slice(0, 24)}>{paragraph}</p>)}
            <div className="moon-columns">
              <div>
                <h3>Good for</h3>
                <ul className="astro-list">
                  {phase.goodFor.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
              <div>
                <h3>Let go of</h3>
                <p>{phase.release}</p>
                <h3>Journal prompt</h3>
                <p><em>{phase.prompt}</em></p>
              </div>
            </div>
            <h3>A small ritual</h3>
            <p>{phase.ritual}</p>
            <ShareRow
              url="/moon-phase-today/"
              text={`Tonight is a ${sky.phase} in ${sky.moon_sign}. Here's what it means:`}
              compact
            />
          </section>
        )}

        {sky && MOON_SIGNS[sky.moon_sign] && (
          <section className="astro-card">
            <p className="astro-eyebrow">The Moon's sign</p>
            <h2>{sign?.glyph} The Moon in {sky.moon_sign}</h2>
            <p>{MOON_SIGNS[sky.moon_sign]}</p>
            {sky.next_sign && (
              <p className="astro-muted">
                The Moon moves into {sky.next_sign.sign} {formatTime(sky.next_sign.at)}.
              </p>
            )}
          </section>
        )}

        {sky?.upcoming?.length > 0 && (
          <section className="astro-card">
            <p className="astro-eyebrow">Coming up</p>
            <h2>Upcoming moon phases</h2>
            <ul className="moon-upcoming">
              {sky.upcoming.slice(0, 6).map((item) => (
                <li key={item.at}>
                  <MoonIcon angle={PHASES[item.key]?.angle ?? 0} size={34} />
                  <span>
                    <strong>{item.phase} in {item.sign}</strong>
                    <span className="astro-muted">{formatMoment(item.at)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {phase && (
          <section className="astro-card moon-card">
            <img src={phase.card.image} alt={phase.card.name} loading="lazy" />
            <div>
              <p className="astro-eyebrow">A tarot card for this moon</p>
              <h2>{phase.card.name}</h2>
              <p>{phase.card.why}</p>
              <p>
                <a href={`/tarot-card-meanings/${phase.card.slug}/`}>Read the {phase.card.name} meaning</a>
              </p>
              <a className="astro-button" href="/daily-tarot-reading/">Pull your daily tarot card</a>
            </div>
          </section>
        )}

        <section className="astro-card" id="birthday">
          <p className="astro-eyebrow">Birthday moon</p>
          <h2>What was the moon on your birthday?</h2>
          <form className="astro-form" onSubmit={lookupBirthday}>
            <div className="astro-date">
              <select aria-label="Month" value={birth.month} onChange={(event) => setBirth({ ...birth, month: event.target.value })}>
                <option value="">Month</option>
                {MONTHS.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
              </select>
              <input
                aria-label="Day"
                inputMode="numeric"
                placeholder="Day"
                value={birth.day}
                onChange={(event) => setBirth({ ...birth, day: event.target.value.replace(/\D/g, '').slice(0, 2) })}
              />
              <input
                aria-label="Year"
                inputMode="numeric"
                placeholder="Year"
                value={birth.year}
                onChange={(event) => setBirth({ ...birth, year: event.target.value.replace(/\D/g, '').slice(0, 4) })}
              />
            </div>
            <button className="astro-button" type="submit" disabled={birthBusy}>{birthBusy ? 'Looking…' : 'Find my moon'}</button>
          </form>
          {birthError && <p className="astro-error">{birthError}</p>}
          {birthResult && birthPhase && (
            <div className="moon-birthday">
              <MoonIcon angle={birthResult.angle} size={72} />
              <div>
                <h3>You were born under a {birthPhase.name} in {birthResult.moon_sign}</h3>
                <p>{birthPhase.energy}</p>
                <p className="astro-muted">
                  People born at this phase often carry its themes: {birthPhase.keywords.join(', ').toLowerCase()}.
                  For your exact moon sign, the houses, and a reading you can ask about, add your birth time and place.{' '}
                  <a href={chartDateHref(birth)}>See your birth chart</a>.
                </p>
              </div>
            </div>
          )}
        </section>

        <section className="astro-section">
          <h2>The eight phases of the moon</h2>
          <p>
            Each month the Moon goes from dark to full and back again as it circles the Earth. Astrology and tarot read
            that cycle as a rhythm for your own life: begin, build, peak, release, rest.
          </p>
          <div className="moon-phase-grid">
            {PHASE_ORDER.map((key) => (
              <article key={key} className={sky?.phase_key === key ? 'current' : ''}>
                <MoonIcon angle={PHASES[key].angle} size={44} />
                <h3>{PHASES[key].name}</h3>
                <p>{PHASES[key].keywords.slice(0, 3).join(' · ')}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="astro-section">
          <h2>Why the moon matters in astrology</h2>
          <p>
            The Sun describes who you are becoming. The Moon describes what you need: comfort, safety, rest, and the
            feelings that move under the surface. Because it moves so quickly, through a whole sign every two or three
            days and through every phase in a month, the Moon is the part of the sky you can feel change day to day.
          </p>
          <p>
            Working with the moon is simple. Notice the phase, notice how you feel, and let the two inform each other.
            Start things as the light grows, finish and release as it wanes, and give yourself permission to rest when
            the Moon goes dark.
          </p>
        </section>

        <section className="astro-section">
          <h2>Keep exploring</h2>
          <ul className="astro-next">
            <li className="astro-next-lead">
              <a href={chartDateHref(birthResult ? birth : null)}>
                <strong>Your birth chart</strong>
                <span>Add your birth time and place for every planet, the houses, and a reading you can ask about.</span>
              </a>
            </li>
            <li><a href="/moon-sign-calculator/"><strong>Your moon sign</strong><span>Where the Moon was when you were born, and what it says you need.</span></a></li>
            <li><a href="/card-of-the-day/"><strong>Tarot card of the day</strong><span>One card as the theme for today.</span></a></li>
          </ul>
        </section>

        <section className="astro-section">
          <h2>Moon phase FAQ</h2>
          <div className="astro-faq">
            {FAQ.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
        <FeaturePromo variant="page" seed="/moon-phase-today/" exclude="/moon-phase-today/" />
      </main>
      <Footer />
    </div>
  );
};

export default MoonPhase;
